import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { InputField } from 'components/form';
import { Box, Button } from '@mui/material';
import { Listing } from 'components/shared/TableListing';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { getLineItemRowStyles, getRemoveWithChildrenIndexes } from './lib';
import { DealSheetFormValues } from './types';
import { CustomerVisibility } from '../../enums';
import {
  LineItemCapsCell,
  LineItemAmountCell,
  LineItemActionsCell,
  LineItemVisibilityCell,
  CustomerVisibilityHeader,
} from './cells';

export const TaxesSection = () => {
  const { control } = useFormContext<DealSheetFormValues>();
  const { fields, append, remove, move } = useFieldArray({ control, name: 'taxes' });

  const watched = useWatch({ control, name: 'taxes' }) ?? [];
  const tableData = fields.map((field, index) => ({ ...field, ...(watched[index] ?? {}) }));

  const handleRemove = (rowIndex: number) => remove(getRemoveWithChildrenIndexes(rowIndex, tableData));

  return (
    <Listing
      tableData={tableData}
      columns={[
        {
          renderKey: 'name',
          header: 'Tax Name',
          bodyStyles: getLineItemRowStyles,
          renderCell: ({ rowIndex }) => (
            <InputField
              control={control}
              label="Tax Name"
              name={`taxes.${rowIndex}.name`}
              inputProps={{ variant: 'outlined' }}
            />
          ),
        },
        {
          renderKey: 'amount',
          header: 'Tax Rate (%)',
          bodyStyles: getLineItemRowStyles,
          renderCell: ({ rowIndex }) => <LineItemAmountCell rowIndex={rowIndex} section="taxes" />,
        },
        {
          renderKey: 'name',
          header: 'Description',
          bodyStyles: getLineItemRowStyles,
          renderCell: ({ rowIndex }) => (
            <InputField
              control={control}
              label="Description"
              name={`taxes.${rowIndex}.description`}
              inputProps={{ variant: 'outlined', multiline: true, minRows: 3 }}
            />
          ),
        },
        {
          width: '16rem',
          renderKey: 'caps',
          header: 'Min/Max caps',
          bodyStyles: getLineItemRowStyles,
          renderCell: ({ rowIndex }) => <LineItemCapsCell rowIndex={rowIndex} section="taxes" />,
        },
        {
          width: '16rem',
          renderKey: 'customerVisibility',
          bodyStyles: getLineItemRowStyles,
          header: <CustomerVisibilityHeader />,
          renderCell: ({ rowIndex }) => <LineItemVisibilityCell rowIndex={rowIndex} section="taxes" />,
        },
        {
          width: '10rem',
          header: 'Action',
          renderKey: 'name',
          bodyStyles: getLineItemRowStyles,
          renderCell: ({ rowIndex }) => (
            <LineItemActionsCell onMove={move} onRemove={handleRemove} rowIndex={rowIndex} rowsCount={fields.length} />
          ),
        },
      ]}
      actionPanelBottom={() => (
        <Box>
          <Button
            size="small"
            onClick={() => {
              append({
                name: '',
                amount: 0,
                cap: null,
                id: uuidv4(),
                type: 'TAX',
                parentId: null,
                description: '',
                amountType: 'percent',
                isEquipmentLvl: false,
                sortOrder: fields.length,
                customerVisibility: CustomerVisibility.Visible,
              });
            }}
          >
            Add row
          </Button>
        </Box>
      )}
    />
  );
};
